import { Button, Select, Tabs, TextInput, UnstyledButton } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { SortableTh, TablePager } from '../purchasing/table.js';
import { AuditTrailPanel } from './AuditTrailPanel.js';
import {
  activityOptions,
  MAINTENANCE_CATEGORIES,
  matchesActivity,
  matchesCategory,
} from './maintenance-filters.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import {
  LoadFailed,
  PmsBadge,
  RowsSkeleton,
  usePmsQuery,
  useVesselChoice,
  VesselPicker,
} from './PmsWorkParts.js';
import {
  type FilterOption,
  filterByPerformer,
  type HistorySortKey,
  historyOptions,
  paginate,
  performerNames,
  type Sort,
  sortHistory,
} from './plan-view.js';
import { REPORT_QUERY_KEYS, ReportsTab } from './ReportsTab.js';
import type { PmsSource } from './source.js';
import type { EquipmentTree, HistoryEntry, JobView } from './types.js';

export interface HistoryScreenProps {
  source: PmsSource;
}

type Data =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; vesselId: string; entries: HistoryEntry[] };

const PAGE_SIZE = 12;

/** Sayfa içi sekmeler (plan M7 Task 31): Work History | Reports | Audit Trail. */
const HISTORY_TABS = ['records', 'reports', 'audit'] as const;

/** Sekme değişince süzgeçler sıfırlanır: iki sekme aynı adres anahtarlarını kullanmaz. */
const TAB_RESET: Record<string, null> = Object.fromEntries(
  [...REPORT_QUERY_KEYS, 'equipment', 'job', 'performer', 'table', 'row'].map((key) => [key, null]),
);

/**
 * Maintenance (PMS) ▸ History (`/pms/history`; plan M7 Task 14d): raporlu iş kayıtları, en yeni
 * önce. İptal edilen kayıt "VOID", gerekçe ve yerine giren kayıtla; iade sonrası iptal "Superseded
 * after return" olarak ayrılır (K14). Ekipman, iş, tarih ve uygulayıcı süzgeçleri; silinmiş
 * ekipman ve iş tanımı da seçilir ve kayıtları görünür [NR496 4.3.2a]. Sıralanır ve sayfalanır.
 * "Reports" sekmesi NR496 ve PMS raporlarının kartlarıdır (önizleme, PDF, Excel, CSV); "Audit Trail"
 * bütün PMS kayıtlarının denetim kaydıdır [NR496 4.6.3] (plan M7 Task 31).
 */
export function HistoryScreen({ source }: HistoryScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const [query, setQuery] = usePmsQuery();
  const choice = useVesselChoice(source, query.get('vessel'));
  const vesselId = choice.vesselId;
  const [data, setData] = useState<Data>({ kind: 'loading' });
  const [tree, setTree] = useState<EquipmentTree | null>(null);
  const [jobs, setJobs] = useState<JobView[]>([]);
  const [reloads, setReloads] = useState(0);
  const [sort, setSort] = useState<Sort<HistorySortKey>>({ key: 'doneOn', dir: 'desc' });
  const [page, setPage] = useState(1);
  const tab = HISTORY_TABS.find((value) => value === query.get('tab')) ?? 'records';
  const filter = {
    equipment: query.get('equipment') ?? '',
    job: query.get('job') ?? '',
    from: query.get('from') ?? '',
    to: query.get('to') ?? '',
    performer: query.get('performer') ?? '',
    // Kategori ve etkinlik (DNV-CP-0206 Sec.2 [4.4] c; ADR 0133 §1): istemcide süzülür
    category: query.get('category') ?? '',
    activity: query.get('activity') ?? '',
  };

  // Süzgeç seçenekleri: ağaç ve kütüphane (silinmişler dahil)
  useEffect(() => {
    if (!vesselId) return;
    let live = true;
    Promise.all([
      source.tree(vesselId).catch(() => null),
      source.jobs ? source.jobs(vesselId).then((list) => list.items) : Promise.resolve([]),
    ])
      .then(([nodes, list]) => {
        if (!live) return;
        setTree(nodes);
        setJobs(list);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [source, vesselId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: süzgeç değişince ve `reloads` ile yeniden okunur
  useEffect(() => {
    if (!vesselId || !source.history) return;
    let live = true;
    setData((current) =>
      current.kind === 'ready' && current.vesselId === vesselId ? current : { kind: 'loading' },
    );
    source
      .history(vesselId, {
        ...(filter.equipment ? { equipment: filter.equipment } : {}),
        ...(filter.job ? { job: filter.job } : {}),
        ...(filter.from ? { from: filter.from } : {}),
        ...(filter.to ? { to: filter.to } : {}),
      })
      .then((entries) => {
        if (live) setData({ kind: 'ready', vesselId, entries });
      })
      .catch((error: unknown) => {
        if (live) setData({ kind: 'error', code: errorCode(error) });
      });
    return () => {
      live = false;
    };
  }, [source, vesselId, filter.equipment, filter.job, filter.from, filter.to, reloads]);

  const entries = data.kind === 'ready' ? data.entries : null;
  const workTypeOf = useMemo(() => new Map(jobs.map((j) => [j.id, j.workType])), [jobs]);
  const rows = useMemo(
    () =>
      sortHistory(
        filterByPerformer(entries ?? [], filter.performer).filter(
          (e) =>
            matchesCategory(e.kind, filter.category) &&
            matchesActivity(e.jobId ? workTypeOf.get(e.jobId) : null, filter.activity),
        ),
        sort,
      ),
    [entries, filter.performer, filter.category, filter.activity, workTypeOf, sort],
  );
  const activities = useMemo(() => activityOptions(jobs.map((j) => j.workType)), [jobs]);
  const pageView = paginate(rows, page, PAGE_SIZE);
  const options = useMemo(
    () => historyOptions(entries ?? [], tree?.nodes ?? [], jobs),
    [entries, tree, jobs],
  );
  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setPage(1);
      setQuery(patch);
    },
    [setQuery],
  );
  const byId = useMemo(() => new Map((entries ?? []).map((e) => [e.id, e])), [entries]);
  /** İptal edilen kaydın yerine giren (düzeltme; K14). */
  const replacement = useMemo(() => {
    const map = new Map<string, HistoryEntry>();
    for (const e of entries ?? []) if (e.supersedesRecordId) map.set(e.supersedesRecordId, e);
    return map;
  }, [entries]);
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.history.${key}`, values);
  const title = t('menu.pmsHistory');
  const failed = choice.failed ?? (data.kind === 'error' ? data.code : null);
  const openWorkOrder = (id: string) => shell?.navigate(`/pms/work-orders?wo=${id}`);
  const label = (option: FilterOption) =>
    option.deleted ? `${option.label} ${k('removed')}` : option.label;
  const filtered = Object.values(filter).some((value) => value !== '');

  if (choice.vessels !== null && choice.vessels.length === 0) {
    return (
      <div className={classes.screen}>
        <PageHeader title={title} />
        <Panel>
          <EmptyState title={t('pms.equipment.noVessel')} />
        </Panel>
      </div>
    );
  }

  const columns: [HistorySortKey, string][] = [
    ['doneOn', k('columns.doneOn')],
    ['woNo', k('columns.woNo')],
    ['equipment', k('columns.equipment')],
    ['job', k('columns.job')],
    ['performedBy', k('columns.performedBy')],
    ['status', k('columns.status')],
  ];

  return (
    <div className={classes.screen} data-pms-screen>
      <PageHeader
        title={title}
        description={k('description')}
        actions={
          <span className={work.headerActions}>
            <VesselPicker
              scope={source.scope}
              vessels={choice.vessels}
              value={vesselId}
              onChange={(id) =>
                update({
                  vessel: id,
                  equipment: null,
                  job: null,
                  from: null,
                  to: null,
                  performer: null,
                })
              }
            />
          </span>
        }
      />
      <Tabs
        value={tab}
        onChange={(value) =>
          update({ ...TAB_RESET, tab: value === 'reports' || value === 'audit' ? value : null })
        }
        className={work.pageTabs}
        keepMounted={false}
      >
        <Tabs.List>
          <Tabs.Tab value="records">{k('tabs.records')}</Tabs.Tab>
          <Tabs.Tab value="reports">{k('tabs.reports')}</Tabs.Tab>
          {source.auditTrail ? <Tabs.Tab value="audit">{k('tabs.audit')}</Tabs.Tab> : null}
        </Tabs.List>
        <Tabs.Panel value="records">
          <Panel title={k('title')}>
            <div className={work.filters}>
              <Select
                label={k('filters.equipment')}
                placeholder={k('filters.all')}
                searchable
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={options.equipment.map((o) => ({ value: o.value, label: label(o) }))}
                value={filter.equipment || null}
                onChange={(value) => update({ equipment: value })}
              />
              <Select
                label={k('filters.job')}
                placeholder={k('filters.all')}
                searchable
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={options.jobs.map((o) => ({ value: o.value, label: label(o) }))}
                value={filter.job || null}
                onChange={(value) => update({ job: value })}
              />
              <DayInput
                label={k('filters.from')}
                value={filter.from || null}
                onChange={(value) => update({ from: value })}
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              />
              <DayInput
                label={k('filters.to')}
                value={filter.to || null}
                onChange={(value) => update({ to: value })}
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              />
              <Select
                label={k('filters.category')}
                placeholder={k('filters.all')}
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={MAINTENANCE_CATEGORIES.map((c) => ({
                  value: c,
                  label: t(`pms.work.categories.${c}`),
                }))}
                value={filter.category || null}
                onChange={(value) => update({ category: value })}
              />
              <Select
                label={k('filters.activity')}
                placeholder={k('filters.all')}
                searchable
                clearable
                clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
                data={activities.map((a) => ({
                  value: a,
                  label: t(`pms.work.workTypes.${a}`, { defaultValue: a }),
                }))}
                value={filter.activity || null}
                onChange={(value) => update({ activity: value })}
              />
              <TextInput
                label={k('filters.performer')}
                leftSection={<IconSearch size={14} aria-hidden />}
                value={filter.performer}
                onChange={(event) => update({ performer: event.currentTarget.value })}
              />
            </div>
            {failed ? (
              <LoadFailed
                code={failed}
                onRetry={() => {
                  choice.retry();
                  setReloads((n) => n + 1);
                }}
              />
            ) : entries === null ? (
              <RowsSkeleton rows={8} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={filtered ? k('noMatch') : k('empty')}
                action={
                  filtered ? (
                    <Button
                      size="xs"
                      variant="light"
                      onClick={() =>
                        update({
                          equipment: null,
                          job: null,
                          from: null,
                          to: null,
                          performer: null,
                        })
                      }
                    >
                      {t('common.clearFilters')}
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <>
                <HistoryTable
                  rows={pageView.rows}
                  columns={columns}
                  sort={sort}
                  onSort={(next) => {
                    setSort(next);
                    setPage(1);
                  }}
                  byId={byId}
                  replacement={replacement}
                  onOpen={openWorkOrder}
                />
                {rows.length > PAGE_SIZE ? (
                  <TablePager
                    page={pageView.page}
                    pages={pageView.pages}
                    summary={t('pms.work.showing', {
                      from: pageView.from,
                      to: pageView.to,
                      total: pageView.total,
                    })}
                    label={t('pms.work.pages', { name: k('title') })}
                    onPage={setPage}
                  />
                ) : null}
              </>
            )}
          </Panel>
        </Tabs.Panel>
        <Tabs.Panel value="reports">
          <ReportsTab source={source} vesselId={vesselId} query={query} update={update} />
        </Tabs.Panel>
        {source.auditTrail ? (
          <Tabs.Panel value="audit">
            <HistoryAudit
              source={source}
              vesselId={vesselId}
              table={query.get('table')}
              rowId={query.get('row')}
              onClear={() => update({ table: null, row: null })}
            />
          </Tabs.Panel>
        ) : null}
      </Tabs>
    </div>
  );
}

/** Bütün PMS kayıtlarının denetim kaydı (K37): gemide yalnız gemide yapılan değişiklikler. */
function HistoryAudit({
  source,
  vesselId,
  table,
  rowId,
  onClear,
}: {
  source: PmsSource;
  vesselId: string | null;
  /** Ekipman detayından gelen süzgeç (`?table=equipment&row=…`). */
  table: string | null;
  rowId: string | null;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const valid = table !== null && /^[a-z_]{1,40}$/.test(table) && rowId !== null;
  const load = useCallback(
    (page: { limit: number; offset: number }) =>
      source.auditTrail && vesselId
        ? source.auditTrail(vesselId, {
            ...page,
            ...(valid ? { table: table as string, rowId: rowId as string } : {}),
          })
        : Promise.reject(new Error('UNAVAILABLE')),
    [source, vesselId, valid, table, rowId],
  );
  return (
    <>
      {valid ? (
        <p className={work.note} role="status">
          {t('pms.work.history.auditFiltered', {
            record: t(`pmsAudit.tables.${table}`, { defaultValue: table }),
          })}{' '}
          <Button size="xs" variant="subtle" onClick={onClear}>
            {t('pms.work.history.auditAll')}
          </Button>
        </p>
      ) : null}
      {vesselId ? (
        <AuditTrailPanel key={`${vesselId}|${table}|${rowId}`} load={load} showRecord={!valid} />
      ) : (
        <RowsSkeleton />
      )}
    </>
  );
}

/** Kayıt tablosu: VOID, iade sonrası iptal ve düzeltme bağı satırda (K14). */
export function HistoryTable({
  rows,
  columns,
  sort,
  onSort,
  byId,
  replacement,
  onOpen,
}: {
  rows: readonly HistoryEntry[];
  columns: [HistorySortKey, string][];
  sort: Sort<HistorySortKey>;
  onSort: (sort: Sort<HistorySortKey>) => void;
  byId: ReadonlyMap<string, HistoryEntry>;
  replacement: ReadonlyMap<string, HistoryEntry>;
  onOpen: (workOrderId: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.history.${key}`, values);
  return (
    <section
      className={classes.tableBox}
      aria-label={t('pms.equipment.tableOf', { name: k('title') })}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
      tabIndex={0}
    >
      <table className={classes.table} data-cards>
        <thead>
          <tr>
            {columns.map(([key, name]) => (
              <SortableTh key={key} column={key} label={name} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => {
            const next = replacement.get(entry.id);
            const previous = entry.supersedesRecordId ? byId.get(entry.supersedesRecordId) : null;
            return (
              <tr key={entry.id} data-voided={entry.status === 'voided' || undefined}>
                <td data-label={k('columns.doneOn')}>{formatDay(entry.doneOn)}</td>
                <td data-label={k('columns.woNo')}>
                  {entry.woNo ? (
                    <UnstyledButton
                      className={work.linkButton}
                      onClick={() => onOpen(entry.workOrderId)}
                    >
                      {entry.woNo}
                    </UnstyledButton>
                  ) : (
                    '—'
                  )}
                </td>
                <td data-label={k('columns.equipment')} className={work.wrapCell}>
                  {entry.equipmentName ?? entry.equipmentCode ?? '—'}
                  {entry.equipmentDeleted ? (
                    <span className={work.note}> {k('removed')}</span>
                  ) : null}
                </td>
                <td data-label={k('columns.job')} className={work.wrapCell}>
                  {[entry.jobCode, entry.jobTitle].filter(Boolean).join(' · ') || '—'}
                  {entry.jobDeleted ? <span className={work.note}> {k('removed')}</span> : null}
                </td>
                <td data-label={k('columns.performedBy')}>{performerNames(entry) || '—'}</td>
                <td data-label={k('columns.status')} className={work.wrapCell}>
                  <span className={work.statusCell}>
                    <span className={work.badgeRow}>
                      <PmsBadge status={entry.status} />
                      {entry.void?.afterReturn ? (
                        <PmsBadge status="scheduled">{k('superseded')}</PmsBadge>
                      ) : null}
                      <PmsBadge status={entry.result === 'fail' ? 'returned' : 'completed'}>
                        {t(`pms.work.results.${entry.result}`, { defaultValue: entry.result })}
                      </PmsBadge>
                    </span>
                    {entry.void ? (
                      <span className={work.note}>
                        {k('voidReason', { reason: entry.void.reason })}
                      </span>
                    ) : null}
                    {next ? (
                      <UnstyledButton
                        className={work.linkButton}
                        onClick={() => onOpen(next.workOrderId)}
                      >
                        {k('replacedBy', { date: formatDay(next.doneOn) })}
                      </UnstyledButton>
                    ) : null}
                    {previous ? <span className={work.note}>{k('correction')}</span> : null}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
